import { defineStore } from 'pinia'
import { ref } from 'vue'
import router from '@/router'
import { App } from '@capacitor/app'
import { Network } from '@capacitor/network'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import { Geolocation, Position } from '@capacitor/geolocation'
import { Capacitor } from '@capacitor/core'

export const useCapacitorStore = defineStore('capacitor', () => {
  // State
  const isNative = ref(Capacitor.isNativePlatform())
  const platform = ref(Capacitor.getPlatform())
  const isOnline = ref(true)
  const currentLocation = ref<Position | null>(null)
  const locationPermissionStatus = ref<'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unknown'>('unknown')
  const isAppActive = ref(true)

  // Device info
  const deviceInfo = ref({
    platform: platform.value,
    isNative: isNative.value,
    operatingSystem: '',
    osVersion: '',
    manufacturer: '',
    model: '',
    webViewVersion: ''
  })

  // Actions
  async function initializePlugins(): Promise<void> {
    console.log('Initializing Capacitor plugins...')

    try {
      // Initialize Network monitoring
      await initializeNetwork()

      // Initialize App state monitoring
      await initializeApp()

      // Initialize StatusBar (native only)
      if (isNative.value) {
        await initializeStatusBar()
      }

      // Hide splash screen
      if (isNative.value) {
        await SplashScreen.hide()
      }

      // Check location permissions
      await checkLocationPermissions()

      console.log('Capacitor plugins initialized successfully')
    } catch (error) {
      console.error('Error initializing Capacitor plugins:', error)
    }
  }

  async function initializeNetwork(): Promise<void> {
    try {
      // Get initial network status
      const status = await Network.getStatus()
      isOnline.value = status.connected

      // Listen for network changes
      Network.addListener('networkStatusChange', (status) => {
        isOnline.value = status.connected
        console.log('Network status changed:', status.connected ? 'online' : 'offline')
      })
    } catch (error) {
      console.error('Error initializing network monitoring:', error)
    }
  }

  async function initializeApp(): Promise<void> {
    try {
      // Listen for app state changes
      App.addListener('appStateChange', ({ isActive }) => {
        isAppActive.value = isActive
        console.log('App state changed:', isActive ? 'active' : 'background')
      })

      // Listen for app URL open (deep links)
      App.addListener('appUrlOpen', (event) => {
        console.log('App opened via URL:', event.url)
        try {
          const url = new URL(event.url)
          const path = url.pathname + url.search
          if (path.startsWith('/poi/') || path.startsWith('/track/')) {
            router.push(path)
          }
        } catch (err) {
          console.error('Error handling deep link:', err)
        }
      })

    } catch (error) {
      console.error('Error initializing app monitoring:', error)
    }
  }

  async function initializeStatusBar(): Promise<void> {
    try {
      await StatusBar.setStyle({ style: Style.Dark })
      await StatusBar.setBackgroundColor({ color: '#2D3748' })
    } catch (error) {
      console.error('Error initializing status bar:', error)
    }
  }

  async function checkLocationPermissions(): Promise<void> {
    // MATT-GEOLOCATION-RESTORED: Required for POI creation
    try {
      const permissions = await Geolocation.checkPermissions()
      locationPermissionStatus.value = permissions.location
      console.log('Location permission status:', permissions.location)
    } catch (error) {
      console.error('Error checking location permissions:', error)
      locationPermissionStatus.value = 'unknown'
    }
  }

  async function requestLocationPermissions(): Promise<boolean> {
    // MATT-GEOLOCATION-RESTORED: Required for POI creation
    try {
      const permissions = await Geolocation.requestPermissions()
      locationPermissionStatus.value = permissions.location
      
      if (permissions.location === 'granted') {
        console.log('Location permissions granted')
        return true
      } else {
        console.log('Location permissions denied')
        return false
      }
    } catch (error) {
      console.error('Error requesting location permissions:', error)
      return false
    }
  }

  async function getCurrentLocation(): Promise<Position | null> {
    // MATT-GEOLOCATION-RESTORED: Required for POI creation
    try {
      if (locationPermissionStatus.value !== 'granted') {
        const granted = await requestLocationPermissions()
        if (!granted) {
          throw new Error('Location permissions not granted')
        }
      }

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000
      })

      currentLocation.value = position
      return position
    } catch (error) {
      console.error('Error getting current location:', error)
      return null
    }
  }

  async function watchLocation(callback: (position: Position) => void): Promise<string | null> {
    /* MATT-GEOLOCATION-DISABLED: Watch location
    try {
      if (locationPermissionStatus.value !== 'granted') {
        const granted = await requestLocationPermissions()
        if (!granted) {
          throw new Error('Location permissions not granted')
        }
      }

      const watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: 30000
        },
        (position, err) => {
          if (err) {
            console.error('Location watch error:', err)
            return
          }
          
          if (position) {
            currentLocation.value = position
            callback(position)
          }
        }
      )

      return watchId
    } catch (error) {
      console.error('Error watching location:', error)
      return null
    }
    */
    console.log('MATT-GEOLOCATION-DISABLED: watchLocation skipped')
    return null
  }

  async function clearLocationWatch(watchId: string): Promise<void> {
    /* MATT-GEOLOCATION-DISABLED: Clear location watch
    try {
      await Geolocation.clearWatch({ id: watchId })
    } catch (error) {
      console.error('Error clearing location watch:', error)
    }
    */
    console.log('MATT-GEOLOCATION-DISABLED: clearLocationWatch skipped')
  }

  async function setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
    if (!isNative.value) return

    try {
      await StatusBar.setStyle({ 
        style: style === 'light' ? Style.Light : Style.Dark 
      })
    } catch (error) {
      console.error('Error setting status bar style:', error)
    }
  }

  async function setStatusBarColor(color: string): Promise<void> {
    if (!isNative.value) return

    try {
      await StatusBar.setBackgroundColor({ color })
    } catch (error) {
      console.error('Error setting status bar color:', error)
    }
  }

  function openAppSettings(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (isNative.value) {
        // This would typically open the app settings
        // Implementation depends on platform-specific plugins
        console.log('Opening app settings...')
        resolve()
      } else {
        reject(new Error('App settings only available on native platforms'))
      }
    })
  }

  return {
    // State
    isNative,
    platform,
    isOnline,
    currentLocation,
    locationPermissionStatus,
    isAppActive,
    deviceInfo,

    // Actions
    initializePlugins,
    checkLocationPermissions,
    requestLocationPermissions,
    getCurrentLocation,
    watchLocation,
    clearLocationWatch,
    setStatusBarStyle,
    setStatusBarColor,
    openAppSettings
  }
})